import { media, styled } from '@nx-kit/styling';
import { PrismicRichtextObject } from 'prismic/types';

/**
 * Homepage hero — variant 3 ("Centered & calm"), the layout the client picked
 * out of the seven that used to sit in a slider here. The other six variants
 * and the Swiper wrapper have been removed.
 *
 * Overlays the full-bleed video from `PageBackground` and reuses the real CMS
 * content. The heights below are the ones the slider had, so the teasers stay
 * exactly where they were.
 */

type Props = {
  className?: string;
  headline: Array<PrismicRichtextObject>;
  subheadline?: string;
  buttonText?: string;
  link: string | null;
};

const Section = styled.section`
  position: relative;
  width: 100%;
  /* Same heights the slider used — this is what keeps the teasers up. */
  height: 440px;

  ${media('md')} {
    height: 560px;
  }
  ${media('lg')} {
    height: 640px;
  }
`;

const Slide = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  color: #fff;
`;

const Inner = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1360px;
  margin: 0 auto;
  padding: 48px clamp(24px, 6vw, 90px) 64px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
`;

const Eyebrow = styled.span`
  display: inline-block;
  font-size: clamp(13px, 1.6vw, 17px);
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  margin-bottom: 14px;
  text-align: center;
  color: ${({ theme }) => theme.global.color.gold};
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.5);
`;

const Title = styled.h2`
  margin: 0;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.03;
  font-size: clamp(36px, 6.5vw, 68px);
  text-align: center;
  max-width: 22ch;
  color: #fff;
  text-shadow: 0 2px 18px rgba(0, 0, 0, 0.45);
`;

const CenterBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin: 0 auto;
  max-width: 700px;
  padding: 0 20px;
`;

const PillButton = styled.a`
  text-decoration: none;
  cursor: pointer;
  display: inline-block;
  margin-top: 32px;
  background: #e63312;
  color: #fff;
  font-weight: 700;
  padding: 16px 40px;
  border-radius: 999px;
  font-size: clamp(15px, 1.6vw, 18px);
  box-shadow: 0 12px 32px rgba(230, 51, 18, 0.4);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const HeroHomepageSlider = ({ className, headline, subheadline, buttonText, link }: Props) => {
  const title = headline?.[0]?.text ?? '';
  const hasCta = Boolean(link && buttonText);
  const ctaProps = { href: link ?? '#', target: '_blank', rel: 'noopener noreferrer' };

  return (
    <Section className={className}>
      <Slide>
        <Inner>
          <CenterBox>
            {subheadline && <Eyebrow>{subheadline}</Eyebrow>}
            <Title>{title}</Title>
            {hasCta && <PillButton {...ctaProps}>{buttonText}</PillButton>}
          </CenterBox>
        </Inner>
      </Slide>
    </Section>
  );
};

export default HeroHomepageSlider;
